import { useMutation } from '@apollo/client';
import { addExperimentVariantMutation } from 'api/mutations/experiments';
import { updateExperimentEditVariants } from 'modules/experiments/takeover/ducks/actions';
import { MutateVariant, MutateVariantResponse } from 'modules/experiments/types';
import { mutateEditExperimentVariantState } from 'modules/experiments/utils';
import { addNegativeToast, errorIdentifier } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useExperimentsTranslations } from '../useTranslations';

const source = 'useAddExperimentVariant';

export const useAddExperimentVariant = ({
  experimentId,
  showErrorToaster = true,
}: MutateVariant) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const errorMessage = `Failed to add variant for experiment id ${experimentId}`;
  const errorToasterMessage = t('errorMessage.addVariant');

  const errorHandler = (error: unknown) => {
    if (showErrorToaster)
      dispatch(
        addNegativeToast(
          errorToasterMessage,
          errorIdentifier({
            errorMessage,
            source,
            error,
          }),
        ),
      );
  };

  const [addExperimentVariant] = useMutation(addExperimentVariantMutation, {
    onError: errorHandler,
  });

  return async (
    copyFromTemplateId: number,
    locale: string,
    newNameOpt?: string,
    updateRedux = true,
  ): Promise<MutateVariantResponse> => {
    const { data } = await addExperimentVariant({
      variables: {
        experimentId,
        copyFromTemplateId,
        newNameOpt,
        ...(locale && { locale }),
      },
    });

    if (data?.createVariation?.variations) {
      const {
        createVariation: { variations },
      } = data;

      // We need to save the original BE variant object to the redux state
      // We have selectors which will mutate this date when a component needs it
      if (updateRedux) {
        dispatch(updateExperimentEditVariants(variations));
      }

      // We mutate the variant list and return the newly created variant back
      const mutatedVariants = mutateEditExperimentVariantState(variations);
      return {
        isSuccess: true,
        mutatedVariant: mutatedVariants[mutatedVariants.length - 1],
      };
    }

    if (data?.createVariation?.message) {
      errorHandler(data.createVariation.message);
    }
    return {
      isSuccess: false,
    };
  };
};
